import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ApplePay, ApplePayButtonLocale} from '@bambora/wallet';
import {ChargeItem} from '../../models/Order';
import {BamboraApplePayWalletSession} from '../../models/PaymentModels';
import {AuthorizedApiService} from '../../services/authorized-api.service';
import {firstValueFrom} from 'rxjs';
import MoneyUtils, {ErrorUtils} from '../../utils/Utils';

export type WalletButtonState = 'pending' | 'ready' | 'unavailable';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-apple-pay-button',
  // Container is ALWAYS in DOM so the <apple-pay-button> Web Component can render in the background.
  template: `<div #container class="apple-pay-button-container"></div>`,
  styles: [`
    :host { display: block; }
    .apple-pay-button-container { height: 56px; overflow: hidden; }
    .apple-pay-button-container ::ng-deep apple-pay-button {
      --apple-pay-button-width: 100%;
      --apple-pay-button-height: 56px;
      --apple-pay-button-border-radius: 16px;
      width: 100%;
      display: block;
    }
  `]
})
export class ApplePayButtonComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

  @Input() totalSek!: number;
  @Input() tipParam!: string;
  @Input() payableRefs?: string;
  @Input() chargeItems?: ChargeItem[];
  @Input() plKey?: string;
  @Input() receiptKey?: string;
  @Input() itemRefs?: any[];
  @Input() declineRoute?: string;

  @Output() paymentComplete = new EventEmitter<{ receiptKey: string }>();
  @Output() paymentFailed = new EventEmitter<any>();
  @Output() stateChange = new EventEmitter<WalletButtonState>();
  @Output() processingChange = new EventEmitter<boolean>();

  state: WalletButtonState = 'pending';
  private applePay?: ApplePay;
  private lastReceiptKey?: string;
  private setupGen = 0;
  private viewReady = false;

  constructor(private api: AuthorizedApiService, private cdr: ChangeDetectorRef) {}

  async ngAfterViewInit(): Promise<void> {
    this.viewReady = true;
    await this.setupWallet();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.viewReady && changes['totalSek'] && !changes['totalSek'].firstChange) {
      await this.setupWallet();
    }
  }

  ngOnDestroy(): void {}

  private setState(s: WalletButtonState) {
    if (this.state !== s) {
      this.state = s;
      this.stateChange.emit(s);
    }
  }

  private async setupWallet(): Promise<void> {
    if (!(this.totalSek > 0)) return;
    const gen = ++this.setupGen;
    try {
      const applePay = await ApplePay.create(
        { sessionProvider: { fetchSession: () => this.fetchSession() } },
        {
          countryCode: MoneyUtils.country(),
          currencyCode: MoneyUtils.currency(),
          supportedNetworks: ['visa', 'masterCard', 'amex'],
          merchantCapabilities: ['supports3DS'],
          total: { label: 'Heynow', amount: this.totalSek.toFixed(2) },
        },
      );
      if (gen !== this.setupGen) return;

      const available = await applePay.isAvailable();
      if (gen !== this.setupGen) return;
      console.log('[ApplePay] isAvailable()', available, 'totalSek=', this.totalSek);

      if (!available) {
        this.applePay = undefined;
        this.setState('unavailable');
        return;
      }

      this.applePay = applePay;
      // Render into the (still hidden) container so the Web Component upgrades in the background.
      this.container.nativeElement.innerHTML = '';
      this.applePay.createButton(this.container.nativeElement, {
        onClick: () => this.startPayment(),
        buttonStyle: 'black',
        buttonType: 'plain',
        // All locales the server sends (sv-SE, nb-NO, da-DK, de-DE, en-GB) are in the SDK's list
        buttonLocale: MoneyUtils.locale() as ApplePayButtonLocale,
      });
      // Give the browser one frame to upgrade the custom element; Apple Pay's button is synchronous-ish.
      await new Promise(r => requestAnimationFrame(() => r(null)));
      if (gen !== this.setupGen) return;
      this.setState('ready');
    } catch (err) {
      if (gen !== this.setupGen) return;
      console.warn('Apple Pay init failed', err);
      this.setState('unavailable');
    }
  }

  private async fetchSession() {
    const session = await firstValueFrom(
      this.api.createBamboraApplePayWalletSession(this.tipParam, this.receiptKey, this.itemRefs, this.plKey, this.payableRefs, this.chargeItems, this.declineRoute),
    );
    this.lastReceiptKey = session.receipt_key;
    return { data: session as BamboraApplePayWalletSession };
  }

  private async startPayment() {
    if (!this.applePay) return;
    // The OS sheet closes before start() resolves (backend authorize is still running) — keep a
    // spinner up for that gap. We leave it on through success since navigation unmounts us.
    this.processingChange.emit(true);
    try {
      await this.applePay.start();
      if (this.lastReceiptKey) {
        this.paymentComplete.emit({ receiptKey: this.lastReceiptKey });
      } else {
        this.processingChange.emit(false);
        this.paymentFailed.emit({});
      }
    } catch (err) {
      this.processingChange.emit(false);
      this.paymentFailed.emit(ErrorUtils.isUserCancel(err) ? { message: 'Betalningen avbruten' } : err);
    }
  }
}
